import { CustomProfileFieldType, FieldOption, type CustomProfileField } from '@logto/schemas';
import { uniqueNamesGenerator, names, NumberDictionary } from 'unique-names-generator';
import { condString } from '@silverhand/essentials';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import * as s from 'superstruct';

import BirdCheer from '@/ui-assets/images/bird-cheer.png';
import Button from '@/components/Button';
import PrimitiveProfileInputField from '@/components/InputFields/PrimitiveProfileInputField';

import AddressSubForm from './AddressSubForm';
import FullnameSubForm from './FullnameSubForm';
import styles from './index.module.scss';
import useFieldLabel from './use-field-label';
import useValidateField from './use-validate-field';
import { useTranslation } from 'react-i18next';
import { provinces } from '@/data/provinces';

type Props = {
  readonly customProfileFields: CustomProfileField[];
  readonly defaultValues?: Record<string, unknown>;
  readonly onSubmit: (values: Record<string, unknown>) => Promise<void>;
};

const ExtraProfileForm = ({ customProfileFields, defaultValues, onSubmit }: Props) => {
  const getFieldLabel = useFieldLabel();
  const validateField = useValidateField();
  const methods = useForm<Record<string, unknown>>({
    reValidateMode: 'onBlur',
    defaultValues,
  });
  const { t } = useTranslation();

  const [isSkipping, setIsSkipping] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
  } = methods;

  const submit = handleSubmit(async (value) => {
    await onSubmit(value);
  });

  const handleSkip = async () => {
    // Get the raw form values without validation
    const formValues = getValues();
    setIsSkipping(true);
    try {
      await onSubmit(formValues);
    } finally {
      setIsSkipping(false);
    }
  };

  const allRequiredFields = customProfileFields.map((f) => ({ ...f, required: true }));

  useEffect(() => {
    const numberDictionary = NumberDictionary.generate({ min: 1000, max: 99999 });
    const randomName: string = uniqueNamesGenerator({
      dictionaries: [names, numberDictionary],
      separator: '',
      length: 2,
      style: 'capital'
    });

    if (allRequiredFields.some((field) => field.name === 'name')) {
      setValue('name', randomName, { shouldValidate: true });
    }
  }, [setValue]);

  return (
    <FormProvider {...methods}>
      <img src={BirdCheer} className={styles.decoration} alt="" width={200} height={176} /> 
      <form className={styles.form} onSubmit={submit}>
        {allRequiredFields.map((field) => {
          if (field.type === CustomProfileFieldType.Fullname) {
            return <FullnameSubForm key={field.name} field={field} />;
          }
          const { name, type, label, description, required } = field;
          return (
            <Controller
              key={name}
              control={control}
              name={name}
              rules={{
                validate: (value) =>
                  validateField(value, { ...field, description: condString(description) }),
              }}
              render={({ field: { onBlur, onChange, value } }) => {
                if (type === CustomProfileFieldType.Address) {
                  return <AddressSubForm field={field} />;
                }

                s.assert(value, s.optional(s.string()));

                const isSubscribeEmailField = type === CustomProfileFieldType.Checkbox && name === 'isSubscribeEmail';
                const isBirthYearField = type === CustomProfileFieldType.Select && name === 'birthYear';
                const isProvinceField = type === CustomProfileFieldType.Select && name === 'province';

                let fieldLabel = label || getFieldLabel(name);
                let customOptions: FieldOption[] | undefined = undefined;

                if (isSubscribeEmailField) {
                  fieldLabel = t('button:email_subscribe_title');
                }

                if (isBirthYearField) {
                  const currentYear = new Date().getFullYear();
                  const startYear = 1917; // พ.ศ. 2460
                  const minimumAge = 7;
                  const yearRange = currentYear - minimumAge - startYear + 1;

                  customOptions = Array.from({ length: yearRange }, (_, i) => ({
                    value: String(startYear + i),
                    label: String(startYear + 543 + i)
                  })).toReversed();
                }

                if (isProvinceField) {
                  customOptions = provinces;
                }

                return (
                  <div className={styles.inputGroup}>
                    {
                      !isSubscribeEmailField && false && (
                        <label className={styles.label} htmlFor={name}>{fieldLabel}</label>
                      )
                    }
                    <PrimitiveProfileInputField
                      {...field}
                      name={name}
                      label={fieldLabel}
                      description={condString(description)}
                      required={required}
                      // required={true}
                      value={value}
                      isDanger={!!errors[name]}
                      errorMessage={errors[name]?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                      customOptions={customOptions}
                    />
                    {
                      isSubscribeEmailField && (
                        <p className={styles.fieldDescription}>
                          {t('button:email_subscribe_description')}
                        </p>
                      )
                    }
                  </div>
                );
              }}
            />
          );
        })}
        <Button 
          title="action.continue" 
          htmlType="submit" 
          isLoading={isSubmitting}
          disabled={isSubmitting || isSkipping} 
        />
        <Button
          className={styles.skipButton}
          title="button:skip"
          type="secondary"
          htmlType="button"
          isLoading={isSkipping}
          disabled={isSubmitting || isSkipping}
          onClick={handleSkip}
        />
        <input hidden type="submit" />
      </form>
    </FormProvider>
  );
};

export default ExtraProfileForm;
